import { UserX, UserCheck } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import type { UserType } from '../../types/settings';

interface ConfirmationModalProps {
  isOpen: boolean;
  user: UserType | null;
  action: 'revoke' | 'grant' | null;
  loading: boolean;
  error: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmationModal({
  isOpen,
  user,
  action,
  loading,
  error,
  onConfirm,
  onCancel
}: ConfirmationModalProps) {
  const darkMode = useTheme((state) => state.theme);

  if (!isOpen || !user || !action) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className={`rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 relative ${
        darkMode 
          ? 'bg-[#0a1414] text-white border border-[#1a2424]' 
          : 'bg-white text-[#053333]'
      }`}>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          {action === 'revoke' ? (
            <UserX className="w-5 h-5 text-red-500" />
          ) : (
            <UserCheck className="w-5 h-5 text-green-500" />
          )}
          {action === 'revoke' ? 'Revoke Access' : 'Grant Access'}
        </h3>
        <p className="mb-5 text-sm">
          Are you sure you want to {action === 'revoke' ? 'revoke access for' : 'grant access to'} <span className="font-bold">{user.name}</span> ({user.email})?
        </p>
        
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className={`px-4 py-2 rounded-lg border transition-all ${
              loading 
                ? 'opacity-50 cursor-not-allowed'
                : darkMode 
                  ? 'border-[#1a2424] text-gray-300 hover:bg-[#050a0a]' 
                  : 'border-gray-300 text-[#373737] hover:bg-gray-100'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg transition-all font-semibold flex items-center gap-2 ${
              loading
                ? 'opacity-50 cursor-not-allowed'
                : action === 'revoke'
                  ? darkMode
                    ? 'bg-red-700 text-white hover:bg-red-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                  : darkMode
                    ? 'bg-[#1a9f6b] text-white hover:bg-[#148856]'
                    : 'bg-[#1a9f6b] text-white hover:bg-[#148856]'
            }`}
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            )}
            {loading 
              ? 'Processing...' 
              : action === 'revoke' ? 'Revoke' : 'Grant'
            }
          </button>
        </div>
      </div>
    </div>
  );
}
